import quizScenario from './scenarios/quiz.json'
import ldManagerScenario from './scenarios/ld-manager.json'

/**
 * Idle sentinel — the value of OnboardingOverlay's `scenarioId` when NO tour is
 * running. Deliberately a non-empty string that is NOT a SCENARIO_REGISTRY key, so
 * it can never be loaded as a scenario.
 */
export const IDLE_SCENARIO = '__idle__'

/**
 * Registry maps scenario IDs to their local config objects.
 * To add a new built-in scenario: import its JSON and add an entry here.
 */
const SCENARIO_REGISTRY = {
  quiz: quizScenario,
  'ld-manager': ldManagerScenario,
}

/** In-memory cache for fetched remote scenarios. */
const remoteCache = {}

/**
 * Converts a glob-style path pattern (with * wildcards) to a regex and tests against currentPath.
 * Example: "/presentation/*" matches "/presentation/abc123"
 */
function matchPath(pattern, currentPath) {
  const escaped = pattern.replace(/[-[\]{}()+?.,\\^$|#\s]/g, '\\$&').replace(/\*/g, '[^/]+')
  return new RegExp(`^${escaped}$`).test(currentPath)
}

/**
 * Returns true if currentPath matches any pattern in patternOrPatterns.
 * Accepts either a single pattern string or an array of pattern strings,
 * allowing a step to be visible across multiple routes.
 *
 * @param {string|string[]} patternOrPatterns
 * @param {string} currentPath
 */
export function matchPathAny(patternOrPatterns, currentPath) {
  const patterns = Array.isArray(patternOrPatterns) ? patternOrPatterns : [patternOrPatterns]
  return patterns.some((p) => matchPath(p, currentPath))
}

/**
 * Returns the first exact (non-wildcard) path from a step's path field.
 * Used to determine where to navigate when a step becomes active.
 * Returns null if all patterns contain wildcards (user must navigate there naturally).
 *
 * @param {string|string[]} patternOrPatterns
 * @returns {string|null}
 */
export function firstExactPath(patternOrPatterns) {
  const patterns = Array.isArray(patternOrPatterns) ? patternOrPatterns : [patternOrPatterns]
  return patterns.find((p) => !p.includes('*')) ?? null
}

/**
 * Resolves a selector expression to a standard CSS selector string.
 *
 * Supported prefixes:
 *   data-testid=value  →  [data-testid="value"]
 *   id=value           →  #value
 *   name=value         →  [name="value"]
 *   (anything else)    →  passed through as-is (raw CSS selector)
 */
export function resolveSelector(expression) {
  if (expression.startsWith('data-testid=')) {
    return `[data-testid="${expression.slice('data-testid='.length)}"]`
  }
  if (expression.startsWith('id=')) {
    return `#${expression.slice('id='.length)}`
  }
  if (expression.startsWith('name=')) {
    return `[name="${expression.slice('name='.length)}"]`
  }
  return expression
}

/**
 * Returns localStorage key names scoped to a given scenario ID.
 * Per-scenario keys prevent cross-scenario state leakage when multiple
 * scenarios are in use (e.g. via URL param).
 *
 * @param {string|undefined} scenarioId
 * @returns {{ step: string, dismissed: string }}
 */
export function storageKeys(scenarioId) {
  const id = scenarioId || IDLE_SCENARIO
  return {
    step: `aha_onboarding_v1_${id}_step`,
    dismissed: `aha_onboarding_v1_${id}_dismissed`,
  }
}

/**
 * localStorage key holding a one-shot "pending scenario" request.
 *
 * Some scenarios must auto-launch in response to an event that happens on a
 * different route than where the scenario starts (e.g. armed when a presentation is
 * created, but its first step anchors on the editor that loads afterward). The
 * arming code writes the scenario id here;
 * the overlay reads + clears it on its next init so the tour runs exactly once.
 */
export const PENDING_SCENARIO_KEY = 'aha_onboarding_v1_pending_scenario'

/**
 * Window event name dispatched by armPendingScenario() right after the one-shot
 * marker is written. The OnboardingOverlay listens for it so a scenario armed on
 * the SAME route the overlay is already mounted on (armed from a page's mounted()
 * AFTER the overlay's created()/init already resolved to the IDLE sentinel) is
 * consumed immediately, instead of waiting for the next route change that may never
 * come. The overlay still re-enters _initScenario(), so the per-scenario variant
 * gate is re-applied — this only re-opens the consume path, it never bypasses gating.
 */
export const ONBOARDING_SCENARIO_ARMED_EVENT = 'aha-onboarding:scenario-armed'

/**
 * sessionStorage key that persists the ?onboarding= URL override across Vue Router
 * redirects. Vue Router drops query params during client-side redirects (e.g. / →
 * /presentations → /presentation/:id). By capturing the param into sessionStorage
 * at the very first beforeEach call — before any redirect fires — we ensure the
 * override is still readable when OnboardingOverlay initialises on the final route.
 *
 * sessionStorage scope (tab-lifetime) is intentional: it acts exactly like having
 * the ?onboarding= param in the URL for the current tab session, without polluting
 * subsequent tabs or after-restart loads.
 */
const ONBOARDING_URL_OVERRIDE_KEY = 'aha_onboarding_v1_url_override'

/**
 * Captures the ?onboarding= query param from a URL search string into sessionStorage.
 * Call this in the router's first beforeEach guard, BEFORE any redirect happens,
 * so the value survives the full redirect chain.
 *
 * No-op when the param is absent — does not clear an existing captured value, so
 * a previous call's capture is preserved through the redirect.
 *
 * @param {string} search  — e.g. "?onboarding=quiz&foo=bar" or "" if absent
 */
export function captureOnboardingUrlOverride(search) {
  try {
    const val = new URLSearchParams(search).get('onboarding')
    if (val) {
      sessionStorage.setItem(ONBOARDING_URL_OVERRIDE_KEY, val)
    }
  } catch (e) {
    // sessionStorage unavailable (private mode, cross-origin iframe) — best-effort.
  }
}

/**
 * Returns the captured ?onboarding= override value, or null if none was captured.
 * Does NOT clear the value; call clearOnboardingUrlOverride() when the overlay
 * has consumed it (to prevent it from re-triggering after a manual page reload).
 *
 * @returns {string|null}
 */
export function readOnboardingUrlOverride() {
  try {
    return sessionStorage.getItem(ONBOARDING_URL_OVERRIDE_KEY)
  } catch (e) {
    return null
  }
}

/**
 * Clears the sessionStorage override flag. Call this once the overlay has
 * successfully initialised a scenario from the URL override, so that a hard
 * reload of the same URL (without ?onboarding=) does not re-trigger the tour.
 */
export function clearOnboardingUrlOverride() {
  try {
    sessionStorage.removeItem(ONBOARDING_URL_OVERRIDE_KEY)
  } catch (e) {
    // best-effort
  }
}

/**
 * Arms a scenario to auto-launch on the overlay's next initialization.
 * One-shot: consumed (and cleared) by consumePendingScenario().
 *
 * @param {string} scenarioId — a SCENARIO_REGISTRY id
 */
export function armPendingScenario(scenarioId) {
  try {
    localStorage.setItem(PENDING_SCENARIO_KEY, scenarioId)
  } catch (e) {
    // localStorage can throw in private-mode / quota-exceeded; arming is best-effort.
    // eslint-disable-next-line no-console
    console.error('[onboarding] failed to arm pending scenario:', e)
  }

  // Notify a mounted OnboardingOverlay that a marker was just armed, so it can
  // consume it on the SAME route without waiting for a route change. Dispatched
  // even when the localStorage write threw — the overlay re-reads the marker via
  // its own guarded peek and simply no-ops if nothing is pending. Best-effort:
  // `window` is undefined in SSR/unit contexts, and dispatchEvent must never break
  // the (non-blocking, best-effort) arm path. (BOO-63 round 4.)
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent(ONBOARDING_SCENARIO_ARMED_EVENT, {
        detail: { scenarioId },
      }))
    }
  } catch (e) {
    // Ignore — overlay also re-inits on route change as a fallback.
  }
}

/**
 * Per-scenario Unleash gate map: scenario id → { flag, variant }. A scenario listed
 * here only renders when getVariant(flag).name === variant, and an auto-launching
 * (armed) scenario must be listed here for the overlay's render gate to open.
 * Scenarios NOT listed (quiz, ld-manager) are reachable only via the ?onboarding=
 * QA override.
 */
export const SCENARIO_VARIANT_GATES = {}

/**
 * Returns the { flag, variant } gate for a scenario id, or null when the scenario
 * is not variant-gated.
 *
 * @param {string} scenarioId
 * @returns {{ flag: string, variant: string } | null}
 */
export function getScenarioVariantGate(scenarioId) {
  return SCENARIO_VARIANT_GATES[scenarioId] || null
}

/**
 * Reads and clears the pending-scenario marker. Returns the armed scenario id,
 * or null when nothing is pending. Clearing on read guarantees the tour fires
 * once and does not re-trigger on subsequent page loads.
 *
 * Also clears the armed scenario's own step/dismissed state so it always launches
 * fresh from step 0 — even if the user happened to run (and dismiss) it before.
 *
 * @returns {string|null}
 */
export function consumePendingScenario() {
  try {
    const id = localStorage.getItem(PENDING_SCENARIO_KEY)
    if (!id) return null
    localStorage.removeItem(PENDING_SCENARIO_KEY)
    const keys = storageKeys(id)
    localStorage.removeItem(keys.step)
    localStorage.removeItem(keys.dismissed)
    return id
  } catch (e) {
    return null
  }
}

/**
 * Loads a scenario by ID or URL.
 *
 * - Local IDs are resolved from SCENARIO_REGISTRY. Returns null for unknown IDs —
 *   no fallback to 'default'. The caller must treat null as "do not launch".
 * - Full HTTPS URLs are fetched from the network and cached in memory,
 *   so they can be hosted anywhere without a redeploy.
 *
 * SECURITY: remote-URL scenarios let a `?onboarding=<url>` link open the tour gate
 * for ANY visitor, and the fetched JSON drives DOM querying and (for click-or-next
 * steps) programmatic clicks on the visitor's authenticated UI — a crafted-link /
 * deceptive-tour vector. Remote fetch is enabled in every environment, INCLUDING
 * production; there is no environment gate. ID-based registry scenarios
 * (quiz/ld-manager) also work everywhere.
 *
 * @param {string|undefined} scenarioIdOrUrl
 * @returns {Promise<{ steps: Array } | null>}
 */
export async function loadScenario(scenarioIdOrUrl) {
  // Empty/falsy id means nothing explicitly requested a scenario — do not launch.
  // (Unknown registry ids also return null below; this closes the empty-id path.)
  if (!scenarioIdOrUrl) return null
  const key = scenarioIdOrUrl

  // Remote URL — fetch once and cache. HTTPS only. Allowed in every environment
  // (see SECURITY above).
  const isRemoteUrl = key.startsWith('https://')
  if (isRemoteUrl) {
    if (remoteCache[key]) return remoteCache[key]
    const res = await fetch(key)
    if (!res.ok) throw new Error(`Failed to fetch onboarding scenario from ${key}: HTTP ${res.status}`)
    const body = await res.json()
    // Some hosting services wrap the payload in an envelope (e.g. { record: {...} }).
    // If the response doesn't have a top-level `steps` array, unwrap the first
    // object-valued property whose value contains `steps`.
    const scenario = body && Array.isArray(body.steps)
      ? body
      : (body && typeof body === 'object'
        ? Object.values(body).find((v) => v && Array.isArray(v.steps))
        : null) ?? body
    remoteCache[key] = scenario
    return scenario
  }

  // Local registry — returns null for unknown IDs (no default fallback).
  // A missing scenario means nothing requested this ID explicitly; the caller
  // must treat null as "do not launch" and abort scenario initialisation.
  return SCENARIO_REGISTRY[key] ?? null
}

/** For testing only — clears the in-memory remote scenario cache between test runs. */
export function _clearRemoteCache() {
  Object.keys(remoteCache).forEach((k) => delete remoteCache[k])
}

/**
 * Replaces `$variable` placeholders in a string with values from the provided map.
 *
 * Supported placeholders:
 *   $name   — the user's display name (first name, or full name)
 *
 * Unknown placeholders are left as-is so future additions don't break old scenarios.
 *
 * @param {string} text
 * @param {Record<string, string>} vars  e.g. { name: 'Alice' }
 * @returns {string}
 */
export function interpolateText(text, vars) {
  if (!text || !vars) return text
  return text.replace(/\$([a-zA-Z_]\w*)/g, (match, key) => {
    return Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : match
  })
}
